export const BASE = 0xac00;
export const CHO_N = 19;
export const JUNG_N = 21;
export const JONG_N = 28;

export interface Jamo {
  readonly cho: number;
  readonly jung: number;
  readonly jong: number;
}

export function isSyllable(ch: string): boolean {
  const c = ch.charCodeAt(0);
  return c >= BASE && c <= 0xd7a3;
}

export function decompose(ch: string): Jamo {
  const c = ch.charCodeAt(0) - BASE;
  return {
    cho: (c / (JUNG_N * JONG_N)) | 0,
    jung: ((c / JONG_N) | 0) % JUNG_N,
    jong: c % JONG_N,
  };
}

export function compose(cho: number, jung: number, jong: number): string {
  return String.fromCharCode(BASE + (cho * JUNG_N + jung) * JONG_N + jong);
}

/** 초성·중성·종성 일치 개수 0..3. P0 에서는 계측 라벨로만 쓴다. */
export function jamoSimilarity(a: string, b: string): number {
  if (!isSyllable(a) || !isSyllable(b)) return 0;
  const x = decompose(a);
  const y = decompose(b);
  return (x.cho === y.cho ? 1 : 0) + (x.jung === y.jung ? 1 : 0) + (x.jong === y.jong ? 1 : 0);
}

/** 사다리 생성이 쓰는 공통 자모 집합. 겹받침·쌍자음·복합모음은 여기서 빠진다. */
export const CHO_COMMON: readonly number[] = [0, 2, 3, 5, 6, 7, 9, 11, 12, 14, 15, 16, 17, 18];
export const JUNG_COMMON: readonly number[] = [0, 2, 4, 6, 8, 12, 13, 17, 18, 20];
export const JONG_COMMON: readonly number[] = [0, 1, 4, 8, 16, 17, 19, 21];

/** 희귀 자모 → 공통 자모. ㄲ→ㄱ, ㄺ→ㄹ, ㅘ→ㅗ 등. */
const CHO_MAP: Readonly<Record<number, number>> = {
  1: 0, // ㄲ→ㄱ
  4: 3, // ㄸ→ㄷ
  8: 7, // ㅃ→ㅂ
  10: 9, // ㅆ→ㅅ
  13: 12, // ㅉ→ㅈ
};
const JUNG_MAP: Readonly<Record<number, number>> = {
  1: 0, // ㅐ→ㅏ
  3: 2, // ㅒ→ㅑ
  5: 4, // ㅔ→ㅓ
  7: 6, // ㅖ→ㅕ
  9: 8, // ㅘ→ㅗ
  10: 8, // ㅙ→ㅗ
  11: 8, // ㅚ→ㅗ
  14: 13, // ㅝ→ㅜ
  15: 13, // ㅞ→ㅜ
  16: 13, // ㅟ→ㅜ
  19: 18, // ㅢ→ㅡ
};
const JONG_MAP: Readonly<Record<number, number>> = {
  2: 1, // ㄲ→ㄱ
  3: 19, // ㄳ→ㅅ
  5: 4, // ㄵ→ㄴ
  6: 4, // ㄶ→ㄴ
  9: 8, // ㄺ→ㄹ
  10: 8, // ㄻ→ㄹ
  11: 8, // ㄼ→ㄹ
  12: 8, // ㄽ→ㄹ
  13: 8, // ㄾ→ㄹ
  14: 8, // ㄿ→ㄹ
  15: 8, // ㅀ→ㄹ
  18: 17, // ㅄ→ㅂ
  20: 19, // ㅆ→ㅅ
  22: 21, // ㅈ→ㅇ 계열 보정
  23: 21,
  24: 1,
  25: 4,
  26: 17,
  27: 21,
};

export function normalizeJamo(j: Jamo): Jamo {
  const cho = CHO_MAP[j.cho];
  const jung = JUNG_MAP[j.jung];
  const jong = JONG_MAP[j.jong];
  return {
    cho: cho === undefined ? j.cho : cho,
    jung: jung === undefined ? j.jung : jung,
    jong: jong === undefined ? j.jong : jong,
  };
}

/** 실제로 자주 쓰이는 기본 모음. 사다리 정렬의 자연스러움 기준이다. */
export const BASIC_JUNG = new Set<number>([0, 4, 8, 13, 18, 20]); // ㅏ ㅓ ㅗ ㅜ ㅡ ㅣ

/** 구개음화 금지: 중성이 ㅑㅕㅛㅠ면 초성 ㄷㅌㅈㅊㅅ 배제 ('뎐·션·쟝' 제거) */
const YOTIZED_JUNG = new Set<number>([2, 6, 12, 17]);
const PALATAL_CHO = new Set<number>([3, 16, 12, 14, 9]);

export function isPalatalizationViolation(cho: number, jung: number): boolean {
  return YOTIZED_JUNG.has(jung) && PALATAL_CHO.has(cho);
}

/**
 * 사다리 생성분에만 적용하는 블록리스트(authored 는 사람이 검수했다).
 * 두 부류다 — ① 비속어·정치·혐오 어감, ② 사문화 음절(현대 한국어 단어·고유명사·구어에서 사실상 쓰이지 않는 것).
 * ②는 2026-09-05 에 `npm run ladder:dump` 로 24제 사다리 전 구간(657자)을 검수해 골랐다(DATA.md §5.3).
 * 기준은 보수적이다 — 표제어·흔한 외래어·구어·고유명사 어느 하나라도 떠오르면 남긴다
 * (예: 볏짚의 '볏', 묠니르의 '묠', 이눔아의 '눔', 그믐의 '믐', 눙치다의 '눙', 하믄의 '믄' 은 남긴다).
 * 항목을 바꾸면 사다리 창이 밀려 스폰이 달라진다 → ENGINE_VERSION 을 올리고 골든을 갱신한다.
 */
export const LADDER_BLOCKLIST: readonly string[] = [
  // ① 비속어·정치·혐오 어감
  '섹',
  '씹',
  '좆',
  '좇',
  '봉',
  '떡',
  '젖',
  '똥',
  '뇨',
  '뇸',
  '창',
  '년',
  '놈',
  '뒈',
  '꺼',
  '닥',
  '족',
  '팔',
  '빡',
  '쭈',
  '뇬', // '년' 변형 비하어. 눈→중성 변형에서 생성된다
  '븅', // '병신' 변형 비하어의 첫 음절. 병→중성 변형에서 생성된다
  // ② 사문화 음절 — ㅑ·ㅕ·ㅛ·ㅠ 계열 (구개음화 가드가 못 잡는 ㄱ·ㄴ·ㄹ·ㅁ·ㅂ·ㅋ·ㅍ 초성)
  '걈', // ㄱ+ㅑ+ㅁ: 표제어·고유명사 없음
  '굑', // ㄱ+ㅛ+ㄱ: 없음
  '굠', // ㄱ+ㅛ+ㅁ: 없음
  '귝', // ㄱ+ㅠ+ㄱ: 없음
  '귬', // ㄱ+ㅠ+ㅁ: 없음
  '녈', // ㄴ+ㅕ+ㄹ: 없음
  '뉸', // ㄴ+ㅠ+ㄴ: 없음
  '럄', // ㄹ+ㅑ+ㅁ: 없음
  '룜', // ㄹ+ㅛ+ㅁ: 없음
  '먀', // ㅁ+ㅑ: 없음 (옛 표기에서만)
  '먄', // ㅁ+ㅑ+ㄴ: 없음
  '먈', // ㅁ+ㅑ+ㄹ: 없음
  '먑', // ㅁ+ㅑ+ㅂ: 없음
  '먕', // ㅁ+ㅑ+ㅇ: 없음
  '몀', // ㅁ+ㅕ+ㅁ: 없음
  '몁', // ㅁ+ㅕ+ㅂ: 없음
  '몃', // ㅁ+ㅕ+ㅅ: 없음 ('몇'과 혼동만 부른다)
  '묜', // ㅁ+ㅛ+ㄴ: 없음
  '묩', // ㅁ+ㅛ+ㅂ: 없음
  '묭', // ㅁ+ㅛ+ㅇ: 없음
  '뮨', // ㅁ+ㅠ+ㄴ: 없음
  '뮵', // ㅁ+ㅠ+ㅂ: 없음
  '뮹', // ㅁ+ㅠ+ㅇ: 없음
  '뱌', // ㅂ+ㅑ: 없음
  '뱍', // ㅂ+ㅑ+ㄱ: 없음
  '뱔', // ㅂ+ㅑ+ㄹ: 없음
  '뱡', // ㅂ+ㅑ+ㅇ: 없음
  '볌', // ㅂ+ㅕ+ㅁ: 없음
  '뵤', // ㅂ+ㅛ: 없음
  '뵥', // ㅂ+ㅛ+ㄱ: 없음
  '뵬', // ㅂ+ㅛ+ㄹ: 없음
  '뵹', // ㅂ+ㅛ+ㅇ: 없음
  '뷱', // ㅂ+ㅠ+ㄱ: 없음
  '뷸', // ㅂ+ㅠ+ㄹ: 없음
  '욤', // ㅇ+ㅛ+ㅁ: 없음
  '욧', // ㅇ+ㅛ+ㅅ: 없음
  '컁', // ㅋ+ㅑ+ㅇ: 없음
  '켝', // ㅋ+ㅕ+ㄱ: 없음
  '켱', // ㅋ+ㅕ+ㅇ: 없음
  '쿅', // ㅋ+ㅛ+ㄱ: 없음
  '큑', // ㅋ+ㅠ+ㄱ: 없음
  '퍌', // ㅍ+ㅑ+ㄹ: 없음
  '퍙', // ㅍ+ㅑ+ㅇ: 없음
  '푤', // ㅍ+ㅛ+ㄹ: 없음
  // ② 사문화 음절 — 기본 모음 계열 (ㅡ 종성 변형과 드문 받침 조합)
  '믈', // 중세 국어 '믈(물)'. 현대 표제어 없음
  '믑', // ㅁ+ㅡ+ㅂ: 없음
  '믕', // ㅁ+ㅡ+ㅇ: 없음
  '븍', // ㅂ+ㅡ+ㄱ: 없음
  '븐', // ㅂ+ㅡ+ㄴ: 없음
  '븜', // ㅂ+ㅡ+ㅁ: 없음
  '븝', // ㅂ+ㅡ+ㅂ: 없음
  '븟', // ㅂ+ㅡ+ㅅ: 없음
  '븡', // ㅂ+ㅡ+ㅇ: 없음
  '츨', // ㅊ+ㅡ+ㄹ: 없음
  '츰', // ㅊ+ㅡ+ㅁ: 방언 '아츰' 외 없음
  '붑', // ㅂ+ㅜ+ㅂ: 없음
  '뭅', // ㅁ+ㅜ+ㅂ: 없음
  '눗', // ㄴ+ㅜ+ㅅ: 없음
  '닙', // ㄴ+ㅣ+ㅂ: 없음
  '솝', // ㅅ+ㅗ+ㅂ: 없음
  '큿', // ㅋ+ㅡ+ㅅ: 없음
  '틍', // ㅌ+ㅡ+ㅇ: 없음
  '픅', // ㅍ+ㅡ+ㄱ: 없음
  '픗', // ㅍ+ㅡ+ㅅ: 없음
  '픙', // ㅍ+ㅡ+ㅇ: 없음
  '훙', // ㅎ+ㅜ+ㅇ: 없음
];

/**
 * 오답 사다리. 팩 빌드 시점에 결정론적으로 확정된다.
 * 초성 변형 → 종성 변형 → 중성 변형 순으로 만들고, 정답 음절 간 라운드로빈으로 인터리브한다.
 * offset 은 호출자가 decoy 스트림에서 뽑아 넘긴다 — "마지막 글자에 함정이 없다"는 편향을 없앤다.
 */
export function buildLadder(answer: string, authored: readonly string[], offset: number): string[] {
  const answerSet = new Set<string>(answer.split(''));
  const blocked = new Set<string>(LADDER_BLOCKLIST);
  const perSyllable: string[][] = [];

  for (const ch of answer) {
    const n = normalizeJamo(decompose(ch));
    const out: string[] = [];
    const seen = new Set<string>();
    const push = (cho: number, jung: number, jong: number): void => {
      if (isPalatalizationViolation(cho, jung)) return;
      const c = compose(cho, jung, jong);
      if (answerSet.has(c) || blocked.has(c) || seen.has(c)) return;
      seen.add(c);
      out.push(c);
    };
    for (const cho of CHO_COMMON) if (cho !== n.cho) push(cho, n.jung, n.jong);
    for (const jong of JONG_COMMON) if (jong !== n.jong) push(n.cho, n.jung, jong);
    for (const jung of JUNG_COMMON) if (jung !== n.jung) push(n.cho, jung, n.jong);
    // 실제로 안 쓰는 음절(걍·먕·뱡·컁·퍙)이 창 앞쪽을 차지하면 함정 구실을 못 한다.
    // 기본 모음 음절을 앞으로 당긴다. 안정 정렬이라 결정론은 유지된다.
    const rank = new Map<string, number>();
    out.forEach((c, i) => rank.set(c, i));
    out.sort((a, b) => {
      const na = BASIC_JUNG.has(decompose(a).jung) ? 0 : 1;
      const nb = BASIC_JUNG.has(decompose(b).jung) ? 0 : 1;
      return na !== nb ? na - nb : (rank.get(a) as number) - (rank.get(b) as number);
    });
    perSyllable.push(out);
  }

  const generated: string[] = [];
  const taken = new Set<string>();
  const len = perSyllable.length;
  const start = len === 0 ? 0 : ((offset % len) + len) % len;
  let round = 0;
  for (;;) {
    let added = 0;
    for (let k = 0; k < len; k++) {
      const list = perSyllable[(start + k) % len] as string[];
      const c = list[round];
      if (c === undefined || taken.has(c)) continue;
      taken.add(c);
      generated.push(c);
      added++;
    }
    if (added === 0) break;
    round++;
  }

  const head: string[] = [];
  const headSeen = new Set<string>();
  for (const c of authored) {
    if (headSeen.has(c) || answerSet.has(c)) continue;
    headSeen.add(c);
    head.push(c);
  }
  return [...head, ...generated.filter((c) => !headSeen.has(c))];
}
